import {useEffect, useState} from 'react';

interface UseVehicleAlbumImageSelectionProps {
  /**
   * Called whenever the image selection changes.
   * Must be a stable reference (e.g. wrapped in useCallback) to avoid
   * re-triggering this effect on every render.
   */
  onSelectionChange?: (selectedImages: string[]) => void;
}

export function useVehicleAlbumImageSelection({
  onSelectionChange,
}: UseVehicleAlbumImageSelectionProps = {}) {
  const [selectedImagesIds, setSelectedImagesIds] = useState<Set<string>>(new Set());

  const getSelectedImages = () => Array.from(selectedImagesIds);

  const isImageSelected = (id: string) => selectedImagesIds.has(id);

  const selectImage = (id: string) => {
    setSelectedImagesIds((prev) => new Set(prev).add(id));
  };

  const selectImagesInBulk = (ids: string[]) => {
    setSelectedImagesIds(new Set(ids));
  };

  const deselectImage = (id: string) => {
    if (selectedImagesIds.has(id)) {
      setSelectedImagesIds((prevSet) => {
        const updatedSet = new Set(prevSet);
        updatedSet.delete(id);
        return updatedSet;
      });
    }
  };

  const clearImageSelection = () => {
    setSelectedImagesIds(new Set());
  };

  const toggleImageSelection = (id: string) => {
    setSelectedImagesIds((prevSet) => {
      const updatedSet = new Set(prevSet);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return updatedSet;
    });
  };

  useEffect(
    function notifyAlbumImageSelectionChange() {
      onSelectionChange?.(Array.from(selectedImagesIds));
    },
    [selectedImagesIds, onSelectionChange]
  );

  return {
    getSelectedImages,
    selectImage,
    deselectImage,
    isImageSelected,
    selectImagesInBulk,
    toggleImageSelection,
    clearImageSelection,
  };
}
